import { Controller } from "@hotwired/stimulus"

// Public (no-login) sub bid page — bid form with chip attachments
// (presign → S3 PUT as files are picked; cache_names ride along on submit),
// revise-bid toggle, styled decline modal + undo, and the ask-a-question
// form. Submit/decline/undo swap the bid panel HTML in place (server-
// rendered partial) — never a full reload.
// Feature 71: once a PDF chip finishes uploading, parseQuote() offers to
// AI-fill amount/inclusions/exclusions/notes from it (highlighted, always
// editable; failures leave the form untouched).
export default class extends Controller {
  static targets = [
    "bidSection", "bidForm", "reviseToggleBtn",
    "amountInput", "inclusionsInput", "exclusionsInput", "notesInput",
    "fileInput", "chipList", "formError", "submitBtn",
    "parseWrap", "parseBtn", "parseError", "aiNote",
    "questionList", "questionEmpty", "questionFormWrap",
    "questionBody", "questionError", "questionSendBtn",
    "declineModal", "declineReason", "declineConfirmBtn"
  ]

  static values = {
    presignUrl: String,
    submitUrl: String,
    declineUrl: String,
    undeclineUrl: String,
    questionUrl: String,
    parseQuoteUrl: String
  }

  connect() {
    // Files already PUT to S3, waiting to be registered on submit:
    // [{ cache_name, filename, content_type, byte_size }]
    this._pendingFiles = []
  }

  // ── Revise toggle ────────────────────────────────────────────────────

  toggleReviseForm(event) {
    event.preventDefault()
    if (!this.hasBidFormTarget) return
    this.bidFormTarget.classList.toggle("hidden")
  }

  // ── Attachments: chip presign → S3 PUT (register happens on submit) ──

  triggerFileInput(event) {
    event.preventDefault()
    this.fileInputTarget.click()
  }

  handleFiles(event) {
    const files = Array.from(event.target.files || [])
    files.forEach(file => this._presignAndPut(file))
    event.target.value = ""
  }

  async _presignAndPut(file) {
    const chip = this._buildUploadingChip(file.name)
    this.chipListTarget.appendChild(chip)

    try {
      const presignRes = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify({
          files: [{
            filename: file.name,
            content_type: file.type || "application/octet-stream",
            byte_size: file.size
          }]
        })
      })
      if (!presignRes.ok) {
        const err = await presignRes.json().catch(() => ({}))
        throw new Error(err.error || "Couldn't start the upload")
      }
      const { uploads } = await presignRes.json()
      const upload = uploads[0]

      const putRes = await fetch(upload.upload_url, {
        method: "PUT",
        headers: { "Content-Type": file.type || upload.content_type },
        body: file
      })
      if (!putRes.ok) throw new Error(`Upload failed (${putRes.status}) — please retry`)

      const pending = {
        cache_name: upload.cache_name,
        filename: upload.filename,
        content_type: file.type || upload.content_type,
        byte_size: file.size
      }
      this._pendingFiles.push(pending)
      this._finishChip(chip, pending)
      this._refreshParseUi()
    } catch (e) {
      chip.remove()
      this._showToast(e.message, "error")
    }
  }

  removePendingFile(event) {
    event.preventDefault()
    const chip = event.currentTarget.closest("[data-cache-name]")
    if (!chip) return
    const cacheName = chip.dataset.cacheName
    this._pendingFiles = this._pendingFiles.filter(f => f.cache_name !== cacheName)
    chip.remove()
    this._refreshParseUi()
  }

  // ── AI quote parsing (feature 71) ────────────────────────────────────

  // The "Fill my bid from this quote" button shows once a PDF attachment
  // has finished uploading.
  _refreshParseUi() {
    if (!this.hasParseWrapTarget) return
    const hasPdf = this._pendingFiles.some(f => /\.pdf$/i.test(f.filename))
    this.parseWrapTarget.classList.toggle("hidden", !hasPdf)
  }

  async parseQuote(event) {
    event.preventDefault()
    const pdf = [...this._pendingFiles].reverse().find(f => /\.pdf$/i.test(f.filename))
    if (!pdf) return

    const btn = this.parseBtnTarget
    const originalHtml = btn.innerHTML
    btn.disabled = true
    btn.innerHTML = '<svg class="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg><span>Reading your quote…</span>'
    this._clearError(this.parseErrorTarget)

    const abort = new AbortController()
    const timer = setTimeout(() => abort.abort(), 90000)
    try {
      const res = await fetch(this.parseQuoteUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify({ cache_name: pdf.cache_name }),
        signal: abort.signal
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || "Couldn't read the quote — please enter your bid manually.")

      this._aiFill(this.amountInputTarget, data.amount)
      this._aiFill(this.inclusionsInputTarget, data.inclusions)
      this._aiFill(this.exclusionsInputTarget, data.exclusions)
      this._aiFill(this.notesInputTarget, data.notes)
      this.aiNoteTarget.classList.remove("hidden")
      this.amountInputTarget.scrollIntoView({ behavior: "smooth", block: "center" })
    } catch (e) {
      const message = e.name === "AbortError"
        ? "Reading the quote timed out — please enter your bid manually."
        : e.message
      this._setError(this.parseErrorTarget, message)
    } finally {
      clearTimeout(timer)
      btn.disabled = false
      btn.innerHTML = originalHtml
    }
  }

  // Fill + subtle highlight so the sub reviews what the AI wrote; the
  // highlight clears as soon as they touch the field.
  _aiFill(input, value) {
    if (value === undefined || value === null || String(value).trim() === "") return
    input.value = value
    input.classList.add("bg-amber-50", "border-amber-300")
    const clear = () => {
      input.classList.remove("bg-amber-50", "border-amber-300")
      input.removeEventListener("input", clear)
    }
    input.addEventListener("input", clear)
  }

  _buildUploadingChip(name) {
    const chip = document.createElement("span")
    chip.className = "inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-[13px] font-medium"
    chip.innerHTML = '<svg class="w-3.5 h-3.5 animate-spin shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>'
    const label = document.createElement("span")
    label.className = "truncate max-w-[180px]"
    label.textContent = name
    chip.appendChild(label)
    return chip
  }

  _finishChip(chip, pending) {
    chip.className = "inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-[13px] font-medium"
    chip.dataset.cacheName = pending.cache_name
    chip.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3.5 h-3.5 shrink-0"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/></svg>'
    const label = document.createElement("span")
    label.className = "truncate max-w-[180px]"
    label.textContent = pending.filename
    chip.appendChild(label)
    const removeBtn = document.createElement("button")
    removeBtn.type = "button"
    removeBtn.className = "inline-flex items-center justify-center w-4 h-4 rounded-full text-gray-400 hover:text-red-600 cursor-pointer shrink-0"
    removeBtn.setAttribute("data-action", "click->public-sub-bid#removePendingFile")
    removeBtn.setAttribute("title", "Remove file")
    removeBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-3.5 h-3.5 pointer-events-none"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>'
    chip.appendChild(removeBtn)
  }

  // ── Submit / revise ──────────────────────────────────────────────────

  async submitBid(event) {
    event.preventDefault()
    const amount = this.amountInputTarget.value.trim()
    if (!amount) {
      this._setError(this.formErrorTarget, "Enter your bid amount.")
      return
    }

    this.submitBtnTarget.disabled = true
    const originalLabel = this.submitBtnTarget.textContent
    this.submitBtnTarget.textContent = "Submitting..."
    try {
      const res = await fetch(this.submitUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify({
          amount,
          inclusions: this.inclusionsInputTarget.value,
          exclusions: this.exclusionsInputTarget.value,
          notes: this.notesInputTarget.value,
          files: this._pendingFiles
        })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || "Something went wrong — please try again")

      this._pendingFiles = []
      this.bidSectionTarget.innerHTML = data.bid_section
      this.bidSectionTarget.scrollIntoView({ behavior: "smooth", block: "start" })
      this._showToast(data.message || "Bid submitted")
    } catch (e) {
      this._setError(this.formErrorTarget, e.message)
      this.submitBtnTarget.disabled = false
      this.submitBtnTarget.textContent = originalLabel
    }
  }

  // ── Decline / undo ───────────────────────────────────────────────────

  showDeclineModal(event) {
    event.preventDefault()
    this.declineModalTarget.classList.remove("hidden")
  }

  hideDeclineModal(event) {
    event?.preventDefault()
    this.declineModalTarget.classList.add("hidden")
  }

  async confirmDecline(event) {
    event.preventDefault()
    this.declineConfirmBtnTarget.disabled = true
    try {
      const res = await fetch(this.declineUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify({ reason: this.declineReasonTarget.value.trim() })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || "Couldn't decline — please try again")

      this.bidSectionTarget.innerHTML = data.bid_section
      if (this.hasQuestionFormWrapTarget) this.questionFormWrapTarget.classList.add("hidden")
      this.hideDeclineModal()
      this._showToast("Invitation declined")
    } catch (e) {
      this._showToast(e.message, "error")
    } finally {
      this.declineConfirmBtnTarget.disabled = false
    }
  }

  async undecline(event) {
    event.preventDefault()
    try {
      const res = await fetch(this.undeclineUrlValue, {
        method: "POST",
        headers: this._jsonHeaders()
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || "Couldn't undo — please try again")

      this.bidSectionTarget.innerHTML = data.bid_section
      if (this.hasQuestionFormWrapTarget) this.questionFormWrapTarget.classList.remove("hidden")
      this._showToast("Welcome back — you can bid again")
    } catch (e) {
      this._showToast(e.message, "error")
    }
  }

  // ── Q&A ──────────────────────────────────────────────────────────────

  async askQuestion(event) {
    event.preventDefault()
    const body = this.questionBodyTarget.value.trim()
    if (!body) {
      this._setError(this.questionErrorTarget, "Type your question first.")
      return
    }

    this.questionSendBtnTarget.disabled = true
    try {
      const res = await fetch(this.questionUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify({ body })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || "Couldn't send your question")

      this.questionListTarget.insertAdjacentHTML("afterbegin", data.row)
      this.questionEmptyTarget.classList.add("hidden")
      this.questionBodyTarget.value = ""
      this._clearError(this.questionErrorTarget)
      this._showToast("Question sent — you'll get the answer here and by email")
    } catch (e) {
      this._setError(this.questionErrorTarget, e.message)
    } finally {
      this.questionSendBtnTarget.disabled = false
    }
  }

  // ── Shared helpers ───────────────────────────────────────────────────

  stopPropagation(event) {
    event.stopPropagation()
  }

  _setError(el, message) {
    el.textContent = message
    el.classList.remove("hidden")
  }

  _clearError(el) {
    el.textContent = ""
    el.classList.add("hidden")
  }

  _jsonHeaders() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
    }
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
